import { useGetItineraryDataQuery, useUpdateItineraryMutation } from '@/src/api/rtkQuery/itineraryApi'
import { TRIPADD } from '@/src/constants/itinerary/insurance'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import { useThemedToast } from '@/src/hooks/ui/useThemedToast'
import { setInsuranceTermsAndConditionsError } from '@/src/redux/slices/itinerarySlice'
import { useAppDispatch } from '@/src/redux/store'

import { Fare } from '@/src/types/itinerary'
import type { ShukranDetail } from '@/src/types/couponTray'
import { isEmpty } from '@/src/utils/generalUtils'
import { getReviewItineraryPayload } from '@/src/utils/itinerary/updatePayloadHelper'
import { takeScreenshot } from '@/src/utils/screenshot'
import { useTranslations } from 'next-intl'
import { useCallback } from 'react'

const useInsuranceCommon = () => {
    const t = useTranslations('itineraryPage.step')
    const tCommon = useTranslations('common')
    const dispatch = useAppDispatch()
    const { showError } = useThemedToast()

    const [updateItinerary, { isLoading: isUpdateItineraryLoading }] = useUpdateItineraryMutation()
    const itineraryId = useGetItineraryId()

    const stepTitle = t('insurance')

    const { selectedInsurances, isInsuranceTermsAndConditionsChecked, fares, fareFamilyEnabledOnReview, flights, shukranData } =
        useGetItineraryDataQuery(
            { itineraryId },
            {
                selectFromResult: ({ data }) => {
                    const { fareFamilyEnabledOnReview } = data?.meta || {}
                    const { selectedInsurances = [], isInsuranceTermsAndConditionsChecked = false } =
                        data?.insurance || {}
                    const fares = data?.faresList?.[0] || []
                    const flights = data?.flights || []
                    const shukranData = (data?.itineraryData?.shukranData as ShukranDetail | null | undefined) ?? null
                    return {
                        fareFamilyEnabledOnReview,
                        selectedInsurances,
                        isInsuranceTermsAndConditionsChecked,
                        fares,
                        flights,
                        shukranData
                    }
                }
            }
        )

    const updateInsuranceHandler = useCallback(
        async ({
            isOptimisticSuccessUpdate = false,
            onSuccess,
            onFailure,
            onValidationFailure = () => {}
        }: {
            isOptimisticSuccessUpdate: boolean
            onSuccess: () => void
            onFailure: () => void
            onValidationFailure?: () => void
        }) => {
            const hasTripAddInsurance = selectedInsurances.some(insurance => insurance.vasProvider === TRIPADD)
            try {
                if (hasTripAddInsurance && !isInsuranceTermsAndConditionsChecked) {
                    dispatch(setInsuranceTermsAndConditionsError(true))
                    showError(tCommon('pleaseAcceptTermsAndConditions'), {
                        id: 'insurance-terms-and-conditions-error'
                    })
                    onValidationFailure?.()
                    return
                }
                const selectedFare = fares.find((fare: Fare) => fare.selected) || ({} as Fare)
                if (!fareFamilyEnabledOnReview && isEmpty(selectedFare)) {
                    onValidationFailure?.()
                    return
                }

                takeScreenshot(itineraryId, 'itinerary', flights)

                // Optimistic update: immediately move to next step
                if (isOptimisticSuccessUpdate) {
                    onSuccess?.()
                }

                const payload = getReviewItineraryPayload({
                    itineraryId,
                    selectedInsurances,
                    selectedFare,
                    shukranData
                })

                const response = await updateItinerary({ payload })

                if (response.error || !response.data.successMetrics) {
                    // API failed, rollback to previous step
                    onFailure?.()
                    showError(tCommon('updateApiError'), {
                        id: 'update-itinerary-error'
                    })
                    return
                } else if (!isOptimisticSuccessUpdate) {
                    onSuccess?.()
                }
            } catch (error) {
                onFailure?.()
                showError('An unexpected error occurred. Please try again.', {
                    id: 'update-itinerary-unexpected-error'
                })
            }
        },
        [selectedInsurances, isInsuranceTermsAndConditionsChecked, fares, shukranData]
    )

    return {
        stepTitle,
        itineraryId,
        isUpdateItineraryLoading,
        updateInsuranceHandler
    }
}

export default useInsuranceCommon
